import { createContext, useContext, type ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import { type Locale, DEFAULT_LOCALE, detectLocale, lookup, interpolate } from './index'
import type { PageKey } from './routes'
import { fr } from './locales/fr'
import type { Messages, DeepPartial } from './locales/fr'
import { en } from './locales/en'
import { nl } from './locales/nl'

export const dictionaries: Record<Locale, DeepPartial<Messages>> = { fr, en, nl }

// ── Locale context (déduit de l'URL) ─────────────────────────────
const LocaleCtx = createContext<Locale>(DEFAULT_LOCALE)

export function LocaleProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const locale = detectLocale(pathname)
  return <LocaleCtx.Provider value={locale}>{children}</LocaleCtx.Provider>
}

export function useLocale(): Locale {
  return useContext(LocaleCtx)
}

/** Traduction : clé dans la langue courante, sinon fallback FR, sinon la clé. */
export function useT() {
  const locale = useLocale()
  const t = (key: string, params?: Record<string, string | number>): string => {
    const inLocale = lookup(dictionaries[locale], key)
    const raw =
      inLocale !== undefined
        ? inLocale
        : (() => {
            const inFr = lookup(dictionaries.fr, key)
            if (inFr === undefined) {
              if (import.meta.env?.DEV) console.warn(`[i18n] clé manquante: ${key}`)
              return key
            }
            return inFr
          })()
    return interpolate(raw, params)
  }
  return { t, locale }
}

// ── RouteMeta context (pageKey de la route courante) ─────────────
const PageKeyCtx = createContext<PageKey | null>(null)

export function RouteMeta({
  pageKey,
  children,
}: {
  pageKey: PageKey
  children: ReactNode
}) {
  return <PageKeyCtx.Provider value={pageKey}>{children}</PageKeyCtx.Provider>
}

export function usePageKey(): PageKey | null {
  return useContext(PageKeyCtx)
}
